<template>
  <button
    class="tw:m-0 tw:flex tw:cursor-pointer tw:items-center tw:leading-none tw:transition-all tw:duration-300 tw:ease-out"
    :class="buttonClasses"
    :disabled="disabled || undefined"
    type="button"
    @click="onClick"
    @mouseout="onMouseOut"
    @mouseover="onMouseOver"
  >
    <div
      class="tw:flex tw:items-center tw:justify-center tw:overflow-hidden"
      :class="{ 'tw:w-[24px]': toolbar, 'tw:w-[32px]': !toolbar && !fab }"
    >
      <AIIcon
        :active="loading"
        class="tw:w-6"
        :class="{
          'tw:fill-ai-primary': !active,
          'tw:fill-white': active,
        }"
        data-testid="icon"
      />
    </div>
    <div
      ref="labelEl"
      class="tw:overflow-hidden tw:whitespace-nowrap tw:text-left tw:font-bold tw:transition-all tw:duration-300 tw:ease-out"
      data-testid="label"
      :style="labelStyle"
    >
      {{ label }}
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed, type CSSProperties, ref } from "vue";

import AIIcon from "shared/modules/AI/components/AIIcon/AIIcon.vue";

interface AISummariseButtonProps {
  active?: boolean;
  disabled?: boolean;
  expand?: boolean;
  expanded?: boolean;
  fab?: boolean;
  label: string;
  loading?: boolean;
  suggest?: boolean;
  toolbar?: boolean;
}

const props = defineProps<AISummariseButtonProps>();

const emit = defineEmits<{
  click: [MouseEvent];
}>();

const labelEl = ref<HTMLDivElement>();
const hover = ref<boolean>(false);

const isExpanded = computed<boolean>(() => props.expand || props.expanded);

const labelStyle = computed<CSSProperties>(() => {
  if (props.expanded) return {};

  if ((hover.value || props.expand) && labelEl.value?.scrollWidth) {
    return {
      maxWidth: `${labelEl.value.scrollWidth + 8}px`,
      paddingInline: "4px",
    };
  }

  return { maxWidth: "0px", paddingInline: "0px" };
});

const BASE_ACTIVE_CLASSES = ["tw:bg-ai-primary", "tw:text-white"] as const;

const buttonClasses = computed<string | string[]>(() => {
  if (props.fab) {
    return fabClasses();
  }

  if (props.toolbar) {
    return toolbarButtonClasses();
  }

  if (props.suggest) {
    return suggestButtonClasses();
  }

  return standardButtonClasses();
});

function toolbarButtonClasses(): string[] {
  const baseToolbarClasses = ["tw:border", "tw:border-solid", "tw:pl-0"];

  if (props.active) {
    const activeToolbarClasses = [
      ...baseToolbarClasses,
      ...BASE_ACTIVE_CLASSES,
      "tw:border-ai-primary",
      "tw:rounded-[14px]",
    ];

    if (isExpanded.value) {
      return [...activeToolbarClasses, "tw:h-[28px]", "tw:pr-1"];
    }

    return [
      ...activeToolbarClasses,
      "tw:h-[var(--s-size-controls-sm)]",
      "tw:pr-0",
    ];
  }

  const inactiveToolbarClasses = [
    "tw:h-[var(--s-size-controls-sm)]",
    "tw:text-ai-primary",
  ];

  if (isExpanded.value || hover.value) {
    return [
      ...baseToolbarClasses,
      ...inactiveToolbarClasses,
      "tw:bg-dark-blue-100",
      "tw:border-dark-blue-200",
      "tw:pr-1",
      "tw:rounded-[14px]",
    ];
  }

  return [
    ...baseToolbarClasses,
    ...inactiveToolbarClasses,
    "tw:bg-white",
    "tw:border-denim-300",
    "tw:pr-0",
    "tw:rounded-xs",
  ];
}

function suggestButtonClasses(): string[] {
  const baseSuggestClasses = [
    "tw:border-ai-primary",
    "tw:border-solid",
    "tw:border",
    "tw:h-[32px]",
    "tw:justify-center",
    "tw:pl-0",
    "tw:rounded-[16px]",
  ];

  if (props.active) {
    return [...baseSuggestClasses, ...BASE_ACTIVE_CLASSES];
  }

  if (hover.value) {
    return [...baseSuggestClasses, "tw:bg-dark-blue-100", "tw:text-ai-primary"];
  }

  return [...baseSuggestClasses, "tw:bg-transparent", "tw:text-ai-primary"];
}

function standardButtonClasses(): string[] {
  const baseStandardClasses = [
    "tw:border-none",
    "tw:h-[32px]",
    "tw:justify-center",
    "tw:pl-0",
    "tw:rounded-[16px]",
  ];

  if (props.active && (isExpanded.value || hover.value)) {
    return [...baseStandardClasses, ...BASE_ACTIVE_CLASSES, "tw:pr-2"];
  }

  if (props.active) {
    return [...baseStandardClasses, ...BASE_ACTIVE_CLASSES, "tw:pr-0"];
  }

  if (isExpanded.value || hover.value) {
    return [
      ...baseStandardClasses,
      "tw:bg-dark-blue-100",
      "tw:pr-2",
      "tw:text-ai-primary",
    ];
  }

  return [
    ...baseStandardClasses,
    "tw:bg-transparent",
    "tw:pr-0",
    "tw:text-ai-primary",
  ];
}

function fabClasses(): string[] {
  return [
    "ai-background-gradient",
    "tw:border-solid",
    "tw:border-white",
    "tw:border",
    "tw:h-[44px]",
    "tw:justify-center",
    "tw:rounded-[22px]",
    "tw:shadow-xl",
    "tw:w-[44px]",
  ];
}

function onClick(event: MouseEvent) {
  emit("click", event);
}

function onMouseOver(): void {
  hover.value = true;
}

function onMouseOut(): void {
  hover.value = false;
}
</script>

<style scoped lang="scss">
@import "shared/css/components/ai";
</style>
